import React, { useState, useEffect, useContext, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import api from '../../services/api';
import { AuthContext } from '../../context/Auth/AuthContext';
import { toast } from 'react-toastify';
import { i18n } from '../../translate/i18n';
import { useHistory } from 'react-router-dom';
import { setKanbanLaneOrder, getKanbanLaneOrder } from '../../services/companyKanbanService';
import { Button, TextField, Paper, FormControl, InputLabel, Select, MenuItem } from '@material-ui/core';
import { format } from 'date-fns';
import { Can } from '../../components/Can';
import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import Title from '../../components/Title';
import KanbanBoard from './KanbanBoard';
import LockIcon from '@material-ui/icons/Lock';

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    overflow: 'hidden',
    ...theme.scrollbarStyles,
    borderRadius: 16,
    background: theme.mode === 'light' ? '#F8FAF9' : '#171923',
    borderColor: theme.mode === 'light' ? 'rgba(15, 61, 46, 0.10)' : 'rgba(255,255,255,0.10)',
    boxShadow: theme.mode === 'light'
      ? '0 12px 35px rgba(15, 61, 46, 0.08)'
      : '0 12px 35px rgba(0, 0, 0, 0.28)',
  },
  button: {
    borderRadius: '10px',
  },
  dateInput: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
    marginRight: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    marginRight: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
    },
  },
  boardToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.mode === 'light' ? 'rgba(15,61,46,0.08)' : 'rgba(255,255,255,0.08)'}`,
    background: theme.mode === 'light'
      ? 'linear-gradient(90deg, #FFFFFF 0%, #F2F8F5 100%)'
      : 'linear-gradient(90deg, #202331 0%, #1B2B27 100%)',
    '@media (max-height: 850px)': {
      gap: theme.spacing(1),
      padding: theme.spacing(0.75, 1.5),
    },
    [theme.breakpoints.down('sm')]: {
      alignItems: 'stretch',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
  },
  boardToolbarCopy: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  boardEyebrow: {
    color: theme.mode === 'light' ? '#183B31' : '#D7F5E8',
    fontSize: '0.74rem',
    fontWeight: 900,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },
  boardHelper: {
    color: theme.mode === 'light' ? '#66756F' : '#96A59F',
    fontSize: '0.75rem',
    marginTop: 2,
    '@media (max-height: 850px)': {
      fontSize: '0.68rem',
      marginTop: 0,
    },
  },
  systemToggleButton: {
    flexShrink: 0,
    borderRadius: 10,
    padding: '8px 14px',
    fontWeight: 800,
    letterSpacing: '0.01em',
    textTransform: 'none',
    color: '#FFFFFF',
    background: 'linear-gradient(135deg, #006B4A 0%, #009E6B 100%)',
    boxShadow: '0 6px 16px rgba(0, 135, 90, 0.22)',
    '@media (max-height: 850px)': {
      padding: '6px 12px',
    },
    '&:hover': {
      background: 'linear-gradient(135deg, #005C40 0%, #008B5E 100%)',
      boxShadow: '0 8px 20px rgba(0, 135, 90, 0.28)',
    },
  },
  systemCount: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 22,
    height: 22,
    marginLeft: theme.spacing(1),
    padding: '0 6px',
    borderRadius: 11,
    background: 'rgba(255,255,255,0.20)',
    fontSize: '0.7rem',
    fontWeight: 900,
  },
  companyBoardArea: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    overflow: 'hidden',
  },
}));

const Kanban = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const [tags, setTags] = useState([]);
	const tagsRef = useRef([]);
	const [ticketsByLane, setTicketsByLane] = useState({});
	const [laneMeta, setLaneMeta] = useState({});
	const laneMetaRef = useRef({});
  const [lanes, setLanes] = useState([]);
  const [systemLanes, setSystemLanes] = useState([]);
  // Alterna entre as etapas da empresa e as etapas automaticas.
  // Os dois conjuntos nunca aparecem juntos.
  const [systemCollapsed, setSystemCollapsed] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const queueIds = (user.queues || []).map(queue => queue.UserQueue?.queueId);
  const isAdmin = user.profile === 'admin';

  // Filtros avançados (visíveis apenas para admin)
  const [filterUserId, setFilterUserId] = useState('all');
  const [filterQueueId, setFilterQueueId] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [queuesList, setQueuesList] = useState([]);

  const [sortOrder, setSortOrder] = useState(() => {
    return localStorage.getItem('sortOrder') || 'ticketNumber';
  });

  const [laneOrder, setLaneOrder] = useState(null);
  const [loadingLaneOrder, setLoadingLaneOrder] = useState(true);

  useEffect(() => {
    localStorage.setItem('sortOrder', sortOrder);
  }, [sortOrder]);

  // Carregar ordem das lanes do banco de dados
  useEffect(() => {
    const loadLaneOrder = async () => {
      try {
        const savedOrder = await getKanbanLaneOrder();
        setLaneOrder(savedOrder);
      } catch (error) {
        console.error('Erro ao carregar ordem das lanes:', error);
      } finally {
        setLoadingLaneOrder(false);
      }
    };

    if (user && user.id) {
      loadLaneOrder();
    }
  }, [user]);

  // Buscar listas de usuários e filas para os filtros (somente admin)
  useEffect(() => {
    if (!isAdmin) return;
    api.get('/users/list').then(({ data }) => setUsersList(data || [])).catch(() => {});
    api.get('/queue').then(({ data }) => setQueuesList(data || [])).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    fetchTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.get('/tag/kanban/');
      const fetchedTags = response.data.lista || [];
      setTags(fetchedTags);
	  tagsRef.current = fetchedTags;
	  await fetchTickets(fetchedTags);
    } catch (error) {
      console.log(error);
    }
  };

	const buildTicketParams = (laneId, pageNumber = 1) => {
	  const params = {
		queueIds: JSON.stringify(queueIds),
			dateStart: startDate,
			dateEnd: endDate,
		laneId: String(laneId),
		pageNumber: String(pageNumber),
		pageSize: '50',
	  };
	  if (isAdmin && filterUserId !== 'all') params.users = JSON.stringify([Number(filterUserId)]);
	  if (isAdmin && filterQueueId !== 'all') params.queueIdFilter = filterQueueId;
	  if (isAdmin && searchText.trim()) params.searchParam = searchText.trim();
	  return params;
	};

	const fetchTickets = async (fetchedTags = tags, preserveLoadedPages = false) => {
    try {
	  const laneIds = ['0', ...fetchedTags.map(tag => String(tag.id))];
	  const currentMeta = laneMetaRef.current || {};
	  const results = await Promise.all(laneIds.map(async laneId => {
		const pagesToLoad = preserveLoadedPages ? Math.max(1, Number(currentMeta[laneId]?.page || 1)) : 1;
		const pageResults = await Promise.all(
		  Array.from({ length: pagesToLoad }, (_, index) =>
			api.get('/ticket/kanban', { params: buildTicketParams(laneId, index + 1) })
		  )
		);
		const unique = new Map();
		pageResults.forEach(response => (response.data.tickets || []).forEach(ticket => unique.set(ticket.id, ticket)));
		const last = pageResults[pageResults.length - 1].data;
		return { laneId, tickets: [...unique.values()], page: pagesToLoad, total: Number(last.count || 0), hasMore: !!last.hasMore, aggregateValue: Number(last.aggregateValue || 0) };
	  }));
	  const nextTickets = {};
	  const nextMeta = {};
	  results.forEach(result => {
		nextTickets[result.laneId] = result.tickets;
		nextMeta[result.laneId] = { page: result.page, total: result.total, hasMore: result.hasMore, loading: false, aggregateValue: result.aggregateValue };
	  });
	  setTicketsByLane(nextTickets);
	  setLaneMeta(nextMeta);
	  laneMetaRef.current = nextMeta;
    } catch (err) {
      console.log(err);
	  setTicketsByLane({});
	  setLaneMeta({});
	  laneMetaRef.current = {};
    }
  };

	const loadMoreLane = async laneId => {
	  const key = String(laneId === 'lane0' ? '0' : laneId);
	  const meta = laneMetaRef.current[key];
	  if (!meta || !meta.hasMore || meta.loading) return;
	  const loadingMeta = { ...laneMetaRef.current, [key]: { ...meta, loading: true } };
	  setLaneMeta(loadingMeta);
	  laneMetaRef.current = loadingMeta;
	  try {
		const nextPage = meta.page + 1;
		const { data } = await api.get('/ticket/kanban', { params: buildTicketParams(key, nextPage) });
		setTicketsByLane(previous => {
		  const unique = new Map((previous[key] || []).map(ticket => [ticket.id, ticket]));
		  (data.tickets || []).forEach(ticket => unique.set(ticket.id, ticket));
		  return { ...previous, [key]: [...unique.values()] };
		});
		const nextMeta = { ...laneMetaRef.current, [key]: { page: nextPage, total: Number(data.count || 0), hasMore: !!data.hasMore, loading: false, aggregateValue: Number(data.aggregateValue || 0) } };
		setLaneMeta(nextMeta);
		laneMetaRef.current = nextMeta;
	  } catch (err) {
		console.log(err);
		const nextMeta = { ...laneMetaRef.current, [key]: { ...meta, loading: false } };
		setLaneMeta(nextMeta);
		laneMetaRef.current = nextMeta;
	  }
	};

  useEffect(() => {
    if (!socket) return;
    const companyId = user.companyId;

    const onAppMessage = data => {
      if (['create', 'update', 'delete', 'refresh'].includes(data.action)) {
		fetchTickets(tagsRef.current, true);
      }
    };

    socket.on(`company-${companyId}-ticket`, onAppMessage);
    socket.on(`company-${companyId}-appMessage`, onAppMessage);
    socket.on(`company-${companyId}-ticketRefresh`, onAppMessage);
    socket.on(`company-${companyId}-kanbanRefresh`, onAppMessage);

    return () => {
      socket.off(`company-${companyId}-ticket`, onAppMessage);
      socket.off(`company-${companyId}-appMessage`, onAppMessage);
      socket.off(`company-${companyId}-ticketRefresh`, onAppMessage);
      socket.off(`company-${companyId}-kanbanRefresh`, onAppMessage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, user.companyId, startDate, endDate, filterUserId, filterQueueId, searchText, isAdmin]);

  const handleSearchClick = () => {
    fetchTickets();
  };

  const handleStartDateChange = event => {
    setStartDate(event.target.value);
  };

  const handleEndDateChange = event => {
    setEndDate(event.target.value);
  };

  const updateTicket = updatedTicket => {
	setTicketsByLane(previous => Object.fromEntries(
	  Object.entries(previous).map(([laneId, laneTickets]) => [
		laneId,
		laneTickets.map(ticket => ticket.id === updatedTicket.id ? updatedTicket : ticket)
	  ])
	));
  };

	const sortLaneTickets = laneTickets => {
	  const sorted = [...(laneTickets || [])];
	  if (sortOrder === 'ticketNumber') sorted.sort((a, b) => a.id - b.id);
	  else if (sortOrder === 'lastMessageTime') sorted.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
	  return sorted;
	};

	const organizeLanes = (fetchedTags = tags, fetchedTicketsByLane = ticketsByLane) => {
    const lanesData = [
      {
        id: 'lane0',
        isSystem: true,
        title: 'Sem Etiqueta',
		tickets: sortLaneTickets(fetchedTicketsByLane['0']),
		total: laneMeta['0']?.total || 0,
		hasMore: !!laneMeta['0']?.hasMore,
		loadingMore: !!laneMeta['0']?.loading,
		onLoadMore: () => loadMoreLane('0'),
		aggregateValue: laneMeta['0']?.aggregateValue || 0,
        color: '#757575',
      },
      ...fetchedTags.map(tag => {
		const key = String(tag.id);
        return {
		  id: key,
          isSystem: !!tag.isSystem,
          title: tag.name,
		  tickets: sortLaneTickets(fetchedTicketsByLane[key]),
		  total: laneMeta[key]?.total || 0,
		  hasMore: !!laneMeta[key]?.hasMore,
		  loadingMore: !!laneMeta[key]?.loading,
		  onLoadMore: () => loadMoreLane(key),
		  aggregateValue: laneMeta[key]?.aggregateValue || 0,
          color: tag.color || '#757575',
        };
      }),
    ];

    // Aplicar ordem personalizada se existir
    if (laneOrder && laneOrder.length > 0) {
      const orderedLanes = [];
      const laneMap = new Map(lanesData.map(lane => [lane.id, lane]));
      
      // Adicionar lanes na ordem salva
      laneOrder.forEach(laneId => {
        if (laneMap.has(laneId)) {
          orderedLanes.push(laneMap.get(laneId));
          laneMap.delete(laneId);
        }
      });
      
      // Adicionar lanes restantes (novas lanes que não estavam na ordem salva)
      laneMap.forEach(lane => orderedLanes.push(lane));
      
      const sys1 = orderedLanes.filter(l => l.isSystem);
      const usr1 = orderedLanes.filter(l => !l.isSystem);
      setSystemLanes(sys1);
      setLanes(usr1);
    } else {
      const sys2 = lanesData.filter(l => l.isSystem);
      const usr2 = lanesData.filter(l => !l.isSystem);
      setSystemLanes(sys2);
      setLanes(usr2);
    }
  };

  useEffect(() => {
    if (!loadingLaneOrder) {
      organizeLanes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tags, ticketsByLane, laneMeta, sortOrder, laneOrder, loadingLaneOrder]);

  const handleCardMove = async (ticketId, targetLaneId) => {
    ticketId = parseInt(ticketId, 10);
    try {
	      if (targetLaneId !== 'lane0') {
	        await api.put(`/ticket-tags/${ticketId}/${targetLaneId}`);
	        toast.success('Ticket Tag atualizado com sucesso!');
	      } else {
	        await api.delete(`/ticket-tags/${ticketId}`);
	        toast.success('Ticket Tag removido!');
      }

      fetchTickets();
    } catch (err) {
      console.log(err);
    }
  };

  const handleAddColumnClick = () => {
    history.push('/tagsKanban');
  };

  const handleSortOrderChange = event => {
    setSortOrder(event.target.value);
  };

  const handleLaneReorder = async (sourceIndex, destinationIndex) => {
    // Verificar se o usuário é admin
    if (user.profile !== 'admin') {
      toast.error('Apenas administradores podem reordenar as lanes do Kanban');
      return;
    }

    const newLanes = Array.from(lanes);
    const [reorderedLane] = newLanes.splice(sourceIndex, 1);
    newLanes.splice(destinationIndex, 0, reorderedLane);
    
    setLanes(newLanes);
    
    // Salvar nova ordem no banco de dados
    const newLaneOrder = newLanes.map(lane => lane.id);
    setLaneOrder(newLaneOrder);
    
    try {
      await setKanbanLaneOrder(newLaneOrder);
      toast.success('Ordem das lanes atualizada com sucesso');
    } catch (error) {
      console.error('Erro ao salvar ordem das lanes:', error);
      toast.error('Erro ao salvar ordem das lanes');
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t('Kanban')}</Title>
        <MainHeaderButtonsWrapper>
          {isAdmin && (
            <FormControl variant="outlined" size="small" className={classes.sortSelect}>
              <InputLabel>Atendente</InputLabel>
              <Select
                value={filterUserId}
                onChange={e => setFilterUserId(e.target.value)}
                label="Atendente"
              >
                <MenuItem value="all">Todos</MenuItem>
                {usersList.map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isAdmin && (
            <FormControl variant="outlined" size="small" className={classes.sortSelect}>
              <InputLabel>Fila</InputLabel>
              <Select
                value={filterQueueId}
                onChange={e => setFilterQueueId(e.target.value)}
                label="Fila"
              >
                <MenuItem value="all">Todas</MenuItem>
                {queuesList.map(q => (
                  <MenuItem key={q.id} value={q.id}>{q.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isAdmin && (
            <TextField
              label="Buscar cliente ou ticket"
              variant="outlined"
              size="small"
              className={classes.dateInput}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearchClick()}
              style={{ minWidth: 200 }}
            />
          )}
          <FormControl
            variant="outlined"
            size="small"
            className={classes.sortSelect}
          >
            <InputLabel htmlFor="sort-order-select">Ordenar por</InputLabel>
            <Select
              native
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Ordenar por"
              inputProps={{
                name: 'sortOrder',
                id: 'sort-order-select',
              }}
            >
              <option value="ticketNumber">Número do Ticket</option>
              <option value="lastMessageTime">Última Mensagem</option>
            </Select>
          </FormControl>
          <TextField
            label="Data de início"
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <TextField
            label="Data de fim"
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            className={classes.dateInput}
            size="small"
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            className={classes.button}
          >
            Buscar
          </Button>
          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddColumnClick}
                className={classes.button}
              >
                + Adicionar colunas
              </Button>
            )}
          />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined" className={classes.mainPaper}>
        {systemLanes.length > 0 && (
          <div className={classes.boardToolbar}>
            <div className={classes.boardToolbarCopy}>
              <span className={classes.boardEyebrow}>Etapas da Estratiza</span>
              <span className={classes.boardHelper}>
                As etapas automáticas continuam ativas e podem ser consultadas sem sair desta tela.
              </span>
            </div>
            <Button
              type="button"
              className={classes.systemToggleButton}
              onClick={() => setSystemCollapsed(previous => !previous)}
              startIcon={<LockIcon style={{ fontSize: 17 }} />}
              aria-expanded={!systemCollapsed}
              aria-controls="automatic-kanban-stages"
            >
              {systemCollapsed ? 'Ver etapas automáticas' : 'Ocultar etapas automáticas'}
              <span className={classes.systemCount}>{systemLanes.length}</span>
            </Button>
          </div>
        )}
        <div id="automatic-kanban-stages" className={classes.companyBoardArea}>
          <KanbanBoard
            lanes={systemCollapsed ? lanes : systemLanes}
            onCardMove={handleCardMove}
            onLaneReorder={handleLaneReorder}
            updateTicket={updateTicket}
            isAdmin={user.profile === 'admin'}
            onLaneEdit={fetchTags}
          />
        </div>
      </Paper>
    </MainContainer>
  );
};

export default Kanban;
